/**
 * Wizard Progress Indicator Component
 *
 * Visual step indicator showing progress through the 5-stage experience creation wizard.
 * Supports clickable navigation to completed stages.
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Story: SCRUM-183 (US-1.27.1-02: Progress Indicator)
 * Location: apps/web/src/components/experiences/creation/WizardProgress.jsx
 */

/**
 * WizardProgress Component
 *
 * @param {Object} props
 * @param {number} props.currentStage - Current stage (1-5)
 * @param {Array<number>} props.completedStages - Array of completed stage numbers
 * @param {number} props.highestCompletedStage - Highest stage user has reached
 * @param {Function} props.onStageClick - Handler for stage navigation (stageNumber) => void
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element}
 */
export default function WizardProgress({
  currentStage = 1,
  completedStages = [],
  highestCompletedStage = 0,
  onStageClick,
  className = ''
}) {
  // Stage definitions with time estimates
  const stages = [
    { number: 1, title: 'Basics', description: 'Name & Details', time: '5 min' },
    { number: 2, title: 'Logistics', description: 'Location & Duration', time: '7 min' },
    { number: 3, title: 'Pricing', description: 'Rates & Availability', time: '8 min' },
    { number: 4, title: 'Media', description: 'Photos & Gallery', time: '5 min' },
    { number: 5, title: 'Review', description: 'Preview & Publish', time: '3 min' }
  ];

  /**
   * Get stage status
   * @param {number} stageNumber - Stage number to check
   * @returns {string} 'completed' | 'current' | 'upcoming'
   */
  const getStageStatus = (stageNumber) => {
    if (completedStages.includes(stageNumber)) {
      return 'completed';
    }
    if (stageNumber === currentStage) {
      return 'current';
    }
    return 'upcoming';
  };

  /**
   * Check if stage is clickable
   * User can navigate to:
   * - Current stage
   * - Any completed stage
   * - Next stage if current is completed
   */
  const isStageClickable = (stageNumber) => {
    return stageNumber <= highestCompletedStage + 1;
  };

  /**
   * Get stage styling classes
   * @param {string} status - Stage status
   * @param {boolean} isClickable - Whether stage is clickable
   * @returns {Object} Styling classes for different elements
   */
  const getStageStyles = (status, isClickable) => {
    switch (status) {
      case 'completed':
        return {
          circle: 'bg-emerald-600 border-emerald-600',
          text: 'text-emerald-700',
          line: 'bg-emerald-600',
          cursor: isClickable ? 'cursor-pointer hover:ring-2 hover:ring-emerald-200' : 'cursor-not-allowed'
        };
      case 'current':
        return {
          circle: 'bg-white border-emerald-600 ring-4 ring-emerald-100',
          text: 'text-emerald-700 font-semibold',
          line: 'bg-gray-300',
          cursor: 'cursor-default'
        };
      case 'upcoming':
        return {
          circle: 'bg-white border-gray-300',
          text: 'text-gray-500',
          line: 'bg-gray-300',
          cursor: 'cursor-not-allowed opacity-60'
        };
      default:
        return {
          circle: 'bg-white border-gray-300',
          text: 'text-gray-500',
          line: 'bg-gray-300',
          cursor: 'cursor-not-allowed'
        };
    }
  };

  /**
   * Handle stage click
   */
  const handleStageClick = (stageNumber) => {
    if (isStageClickable(stageNumber) && onStageClick) {
      onStageClick(stageNumber);
    }
  };

  // Calculate overall progress percentage
  const progressPercentage = Math.round((completedStages.length / stages.length) * 100);

  return (
    <div className={className}>
      {/* Desktop Progress Indicator (horizontal steps) */}
      <nav aria-label="Wizard progress" className="hidden md:block">
        <ol className="flex items-center justify-between">
          {stages.map((stage, index) => {
            const status = getStageStatus(stage.number);
            const isClickable = isStageClickable(stage.number);
            const styles = getStageStyles(status, isClickable);
            const isLastStage = index === stages.length - 1;

            return (
              <li key={stage.number} className="flex items-center flex-1">
                {/* Stage Item */}
                <div className="flex flex-col items-center relative flex-1">
                  {/* Circle with Number or Checkmark */}
                  <button
                    type="button"
                    onClick={() => handleStageClick(stage.number)}
                    disabled={!isClickable}
                    className={`
                      w-12 h-12 rounded-full border-2 flex items-center justify-center
                      transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                      ${styles.circle}
                      ${styles.cursor}
                    `}
                    aria-label={`${stage.title}: ${stage.description}`}
                    aria-current={status === 'current' ? 'step' : undefined}
                  >
                    {status === 'completed' ? (
                      // Checkmark icon for completed stages
                      <svg
                        className="w-6 h-6 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      // Stage number for current/upcoming stages
                      <span
                        className={`
                          text-sm font-semibold
                          ${status === 'current' ? 'text-emerald-600' : 'text-gray-400'}
                        `}
                      >
                        {stage.number}
                      </span>
                    )}
                  </button>

                  {/* Stage Title, Description, and Time */}
                  <div className="mt-3 text-center">
                    <p className={`text-sm ${styles.text} transition-colors duration-300`}>
                      {stage.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {stage.description}
                    </p>
                    <p className="text-xs text-gray-400 mt-1 font-medium">
                      ~{stage.time}
                    </p>
                  </div>
                </div>

                {/* Connecting Line (not shown after last stage) */}
                {!isLastStage && (
                  <div
                    className={`
                      h-0.5 flex-1 mx-3 transition-colors duration-300
                      ${styles.line}
                    `}
                    style={{ marginTop: '-4.5rem' }}
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Mobile Progress Bar */}
      <div className="md:hidden">
        <div className="flex items-center justify-between text-sm text-gray-700 mb-2">
          <span className="font-medium">Step {currentStage} of {stages.length}</span>
          <span className="text-xs text-gray-500">{progressPercentage}% Complete</span>
        </div>
        <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-600 transition-all duration-300 ease-out"
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-valuenow={progressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Wizard progress: ${progressPercentage}% complete`}
          />
        </div>
        {/* Current stage title on mobile */}
        <div className="mt-2 text-center">
          <p className="text-sm font-semibold text-emerald-700">
            {stages[currentStage - 1]?.title}
          </p>
          <p className="text-xs text-gray-500">
            {stages[currentStage - 1]?.description} (~{stages[currentStage - 1]?.time})
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Preset configurations for common use cases
 */
export const WizardProgressPresets = {
  // Standard wizard progress
  standard: {
    className: 'mb-8 pb-6 border-b border-gray-200'
  },

  // Compact version (no border)
  compact: {
    className: 'mb-6'
  },

  // Sticky header version
  sticky: {
    className: 'sticky top-0 z-10 bg-white shadow-sm px-6 py-4 mb-6'
  }
};
